import { round } from 'lodash-es'
import { SceneCommentOffsetDirection, DEFAULT_SCENE_COMMENT_OFFSET_DISTANCE } from './constants'
import { CoordinatePointValue } from './types'

/**
 * Rounds replace coordinates to mitigate float noise from Three.js BufferGeometry,
 * which typically becomes visible beyond roughly the 9th decimal digit.
 */
export const roundReplaceCoordinate = (value: number, numDecimals: number | null): number =>
  numDecimals === null ? value : round(value, numDecimals)

export const roundReplaceCoordinates = (
  x: number,
  y: number,
  z: number,
  axisNumDecimals: {
    x: number | null
    y: number | null
    z: number | null
  },
): CoordinatePointValue => ({
  x: roundReplaceCoordinate(x, axisNumDecimals.x),
  y: roundReplaceCoordinate(y, axisNumDecimals.y),
  z: roundReplaceCoordinate(z, axisNumDecimals.z),
})

const directionVectorByOffsetDirection: Record<
  SceneCommentOffsetDirection,
  { x: number; y: number }
> = {
  bottom: { x: 0, y: -1 },
  top: { x: 0, y: 1 },
  right: { x: 1, y: 0 },
  left: { x: -1, y: 0 },
  'bottom-left': { x: -1, y: -1 },
  'bottom-right': { x: 1, y: -1 },
  'top-left': { x: -1, y: 1 },
  'top-right': { x: 1, y: 1 },
}

export const calculateSceneCommentCenterFromCalloutPoint = ({
  calloutPoint,
  offsetDirection,
  offsetDistance,
}: {
  calloutPoint: CoordinatePointValue
  offsetDirection: SceneCommentOffsetDirection
  offsetDistance: number
}): CoordinatePointValue => {
  const baseDirection = directionVectorByOffsetDirection[offsetDirection]
  const directionLength = Math.hypot(baseDirection.x, baseDirection.y)
  const normalizedDirection = {
    x: baseDirection.x / directionLength,
    y: baseDirection.y / directionLength,
  }
  const clampedDistance = Number.isFinite(offsetDistance)
    ? Math.max(0, offsetDistance)
    : DEFAULT_SCENE_COMMENT_OFFSET_DISTANCE

  return {
    x: calloutPoint.x + normalizedDirection.x * clampedDistance,
    y: calloutPoint.y + normalizedDirection.y * clampedDistance,
    z: calloutPoint.z,
  }
}
